/**
 * Sanitizes API response payload for logging Removes PHI/PII from both field
 * names and string values
 */

// PHI field names to block entirely
const blockedFields = new Set([
  'ssn',
  'ssnlast4',
  'dateofbirth',
  'dob',
  'firstname',
  'lastname',
  'fullname',
  'email',
  'phonenumber',
  'phone',
  'address',
  'street',
  'street2',
  'city',
  'zip',
  'mailingaddress',
  'bankaccount',
  'routingnumber',
  'cardnumber',
  'cvv',
])

/**
 * Sanitizes string values by redacting PHI patterns
 *
 * @param {string} str - String to sanitize
 * @returns {string} - Sanitized string
 */
const sanitizeString = str => {
  if (typeof str !== 'string') return str

  return (
    str
      // SSN: 123-45-6789 or 123456789
      .replace(/\b\d{3}-?\d{2}-?\d{4}\b/g, '[SSN_REDACTED]')
      // Email
      .replace(
        /\b[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}\b/g,
        '[EMAIL_REDACTED]',
      )
      // Phone: various formats including (555) 123-4567
      .replace(
        /(\+1[-.\s]?)?\(?\d{3}\)?[-.\s]?\d{3}[-.\s]?\d{4}\b/g,
        '[PHONE_REDACTED]',
      )
      // Date of birth patterns: 1990-01-15, 01/15/1990, etc.
      .replace(
        /\b(19|20)\d{2}[-/](0[1-9]|1[0-2])[-/](0[1-9]|[12]\d|3[01])\b/g,
        '[DOB_REDACTED]',
      )
      .replace(
        /\b(0[1-9]|1[0-2])[-/](0[1-9]|[12]\d|3[01])[-/](19|20)\d{2}\b/g,
        '[DOB_REDACTED]',
      )
  )
}

/**
 * Recursively sanitizes an object, redacting PHI fields and patterns
 *
 * @param {any} obj - Object to sanitize
 * @returns {any} - Sanitized object
 */
const sanitize = obj => {
  if (typeof obj === 'string') {
    return sanitizeString(obj)
  }

  if (Array.isArray(obj)) {
    return obj.map(sanitize)
  }

  if (obj && typeof obj === 'object') {
    const result = {}
    for (const [key, value] of Object.entries(obj)) {
      if (blockedFields.has(key.toLowerCase())) {
        result[key] = '[REDACTED]'
      } else {
        result[key] = sanitize(value)
      }
    }
    return result
  }

  return obj
}

/**
 * Sanitizes API response payload for safe logging Handles both RSAA
 * payload.response pattern and direct payloads
 *
 * @param {object} payload - API response payload
 * @returns {object | null} - Sanitized payload safe for logging
 */
export const sanitizePayloadForLogging = payload => {
  if (!payload) return null

  // Handle RSAA pattern where response is nested
  const data = payload.response || payload

  if (typeof data === 'string') {
    return sanitizeString(data)
  }

  return sanitize(data)
}
